import lodash from 'lodash';
import moment from 'moment';

import { node, nodeWithArgs, toQuery, toMutation } from 'common/util/queryHelper';
import { queryGraphQL, mutateGraphQL } from 'common/util/query';
import { addApplicationMessage } from 'common/actions/applicationMessagesActions';
import { getTimezoneOffset } from 'common/selectors/index';
import { dataSourceLogActions } from '../utils/constants';

export const LOAD_DATA_SOURCE_LIST = 'LOAD_DATA_SOURCE_LIST';
export const LOAD_DATA_SOURCE = 'LOAD_DATA_SOURCE';
export const UNLOAD_DATA_SOURCE = 'UNLOAD_DATA_SOURCE';
export const DATA_SOURCE_ERRORS = 'DATA_SOURCE_ERRORS';
export const WRITE_DATA_SOURCE_VALUES = 'WRITE_DATA_SOURCE_VALUES';
export const WRITE_DATA_SOURCE_SAVE_SUCCESS = 'WRITE_DATA_SOURCE_SAVE_SUCCESS';
export const LOAD_DATA_SOURCE_LOG = 'LOAD_DATA_SOURCE_LOG';
export const DATA_SOURCE_SUBMITTING = 'DATA_SOURCE_SUBMITTING';

export function loadDataSourceList(institutionId, { query, type } = {}) {
    return async dispatch => {
        const queryNode = nodeWithArgs('getDataSourcesForInstitution', {
            institutionId,
            query,
            type
        }, [
            'id',
            'name',
            'type',
            'description',
            'institutionId',
            'parameters'
        ]);

        const dataSourcesCountByInstitutionQuery = nodeWithArgs('dataSourcesCountByInstitution', {
            institutionId
        });

        const response = await queryGraphQL(toQuery([queryNode, dataSourcesCountByInstitutionQuery]));
        if (response.errors) {
            dispatch(addApplicationMessage('error', 'An error occurred when loading the data sources'));
        } else {
            const { getDataSourcesForInstitution, dataSourcesCountByInstitution } = response.data;
            dispatch(writeDataSources(getDataSourcesForInstitution, dataSourcesCountByInstitution));
        }
    };
}

export const buildLoadDataSourceQuery = dataSourceId => {
    const queryNode = nodeWithArgs(
        'getDataSource',
        {
            dataSourceId,
        },
        ['id', 'name', 'type', 'description', 'institutionId', 'parameters'],
    );
    const descriptorDataTypes = node('descriptorDataTypes');

    return [queryNode, descriptorDataTypes];
};

export function loadDataSource(dataSourceId) {
    return async dispatch => {
        dispatch(unloadDataSource());
        const response = await queryGraphQL(toQuery(buildLoadDataSourceQuery(dataSourceId)));
        if (response.errors) {
            dispatch(addApplicationMessage('error', 'An error occurred when loading the data sources'));
        } else {
            const { getDataSource, descriptorDataTypes } = response.data;
            dispatch(writeDataSource(getDataSource, descriptorDataTypes, false));
            return {
                ...getDataSource,
                parameters: JSON.parse(getDataSource.parameters)
            };
        }
    };
}

export function unloadDataSource() {
    return dispatch => dispatch(unloadDataSourceAction());
}

export function writeDataSourceValues(values){
    return dispatch => {
        const dataSourceValues = lodash.map(values, (v, index) => {
            return {...v, order: index + 1};
        });
        dispatch(writeDataSourceValuesAction(dataSourceValues));
    };
}

export function saveDataSource(institutionId, parametersToValidate, displayToast = true) {
    return async (dispatch, getState) => {
        const storeState = getState();
        const { dataSource, values, dataTypes } = storeState.dataSourceEditor;
        const dataSourceLogs = storeState.dataSourceLogs || {};

        const errors = validateDataSource(dataSource, values, dataTypes, parametersToValidate);
        if (errors.hasErrors) {
            dispatch(addApplicationMessage('error', 'Please delete the duplicate Data Source values before saving.'));
            dispatch(writeDataSourceErrors(errors));
            return errors;
        }
        dispatch(dataSourceSubmitting(true));
        const { id, name, description, type } = dataSource;
        const parametersToSave = lodash.cloneDeep(dataSource.parameters || {});
        parametersToSave.values = lodash.map(values, v => lodash.pick(v, ['value', 'text', 'dataType', 'order', 'status']));
        let parameters = JSON.stringify(parametersToSave);

        const query = nodeWithArgs('updateDataSourceMetadata', {
            id,
            name,
            description,
            type,
            institutionId,
            parameters,
            dataSourceLogs
        }, [
            'id',
            'name',
            'description',
            'type',
            'institutionId',
            'parameters'
        ]);

        const response = await mutateGraphQL(toMutation(query));
        if (response.errors && displayToast) {
            dispatch(addApplicationMessage('error', 'An error occurred when saving the data source.'));
            return response.errors;
        } else {
            displayToast && dispatch(addApplicationMessage('success', getDataSourceMessage(dataSourceLogs.action)));
            dispatch(writeDataSourceSaveSuccess());
            dispatch(writeDataSource(response.data.updateDataSourceMetadata, null, false));
        }
    };
}

export function downloadDataSource() {
    return async (dispatch, getState) => {

        const storeState = getState();
        const { dataSource } = storeState.dataSourceEditor;
        const tzOffset = getTimezoneOffset(storeState);
        const query = nodeWithArgs('getValuesForDataSource', {
            dataSourceId: dataSource.id
        }, [
            'value',
            'status',
            'createdAt',
            'updatedAt',
            'createdBy',
            'updatedBy'
        ]);

        const response = await mutateGraphQL(toQuery(query));
        if (response.errors) {
            dispatch(addApplicationMessage('error', 'An error occurred generating the report.'));
            return response.errors;
        } 
        const values = response.data.getValuesForDataSource;
        
        const csv = generateCSV(values, tzOffset);
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${dataSource.name}.csv`;
        a.click();

    };
}

function formatDate(date, tzOffset) {
    if (!date) {
        return '';
    }

    return moment(date).utcOffset(tzOffset).format('MM-DD-YYYY');
}

function generateCSV(values, tzOffset) {
    const headers = ['Value', 'Status', 'Date Created', 'Date Last Modified', 'Created By', 'Last Modified By'];
    const rows = values.map(item => [
        // Convert value to string to ensure proper CSV formatting when value includes commas
        `"${item.value}"`,
        item.status,
        formatDate(item.createdAt, tzOffset),
        formatDate(item.updatedAt, tzOffset),
        item.createdBy,
        item.updatedBy
    ]);

    const csvRows = [
        headers,
        ...rows
    ].map(row => row.join(','));

    return csvRows.join('\n');
}

const getDataSourceMessage = (action) => {
    switch (action) {
        case dataSourceLogActions.created:
            return 'This item has been successfully added as a data source.';
        case dataSourceLogActions.deactivated:
            return 'Data source item was successfully deactivated.';
        case dataSourceLogActions.restored:
            return 'Data source item was successfully restored.';
        default:
            return 'Data source saved successfully!';
    }
};

export const loadDataSourceLog = (dataSourceLog) => {
    return {
        type: LOAD_DATA_SOURCE_LOG,
        payload: dataSourceLog
    };
};

export const dataSourceSubmitting = (value) => {
    return {
        type: DATA_SOURCE_SUBMITTING,
        payload: value,
    };
};

function writeDataSources(dataSources, dataSourcesTotalCount) {
    return {
        type: LOAD_DATA_SOURCE_LIST,
        payload: {
            dataSources,
            dataSourcesTotalCount
        }
    };
}

export function writeDataSource(dataSource, descriptorDataTypes, isSaveSuccessful) {
    return {
        type: LOAD_DATA_SOURCE,
        payload: {
            dataSource,
            descriptorDataTypes,
            isSaveSuccessful
        }
    };
}

function writeDataSourceValuesAction(values) {
    return {
        type: WRITE_DATA_SOURCE_VALUES,
        payload: {
            values
        }
    };
}

function writeDataSourceSaveSuccess() {
    return {
        type: WRITE_DATA_SOURCE_SAVE_SUCCESS
    };
}

function writeDataSourceErrors(errors) {
    return {
        type: DATA_SOURCE_ERRORS,
        payload: errors
    };
}

function unloadDataSourceAction() {
    return {
        type: UNLOAD_DATA_SOURCE,
    };
}

function validateDataSource(dataSource, values, dataTypes, parametersToValidate) {
    const { name, description, type, parameters } = dataSource;
    const errors = {
        name: null,
        description: null,
        type: null,
        parameters: {},
        values: null,
        hasErrors: false
    };
    if (lodash.isNil(name) || lodash.isEmpty(name)) {
        errors.name = 'Name cannot be blank.';
        errors.hasErrors = true;
    }
    if (lodash.isNil(description) || lodash.isEmpty(description)) {
        errors.description = 'Description cannot be blank';
        errors.hasErrors = true;
    }
    if (lodash.isNil(type) || lodash.isEmpty(type)) {
        errors.type = 'Type cannot be blank';
        errors.hasErrors = true;
    }
    if (type === 'UserDefinedDataSource') {
        const { dataType } = parameters;
        if (!dataType || !lodash.includes(dataTypes, dataType.toUpperCase())) {
            errors.parameters.dataType = 'The selected data type is missing or not allowed.';
            errors.hasErrors = true;
        }
        const valueValidation = validateValues(values, dataType.toUpperCase());
        errors.values = valueValidation.errors;
        errors.hasErrors = errors.hasErrors || valueValidation.hasErrors;
    } else {
        lodash.forEach(parametersToValidate, key => {
            const value = parameters[key];
            if (lodash.isNil(value) || ('' === value)) {
                errors.parameters[key] = `The ${key} parameter cannot be blank.`;
                errors.hasErrors = true;
            }
        });
    }
    return errors;
}

function validateValues(values, dataType) {
    let hasErrors = false;
    const distinctValues = new Set();
    const distinctText = new Set();
    let errors = lodash.map(values, ({value, text, order}) => {
        value = typeof value === 'string' ? value.trim() : value;
        text = text && text.trim();
        const error = {
            value: null,
            text: null,
            dataType: null,
            order: null,
            hasErrors: false
        };
        if (!isValidValue(value, dataType)) {
            error.value = 'Value cannot be blank.';
            hasErrors = true;
            error.hasErrors = true;
        } else {
            if (distinctValues.has(value)) {
                error.value = `This value '${value}' is already present`;
                hasErrors = true;
                error.hasErrors = true;
            } else {
                distinctValues.add(value);
            }
        }
        if (lodash.isNil(text) || lodash.isEmpty(text)) {
            error.text = 'Text cannot be blank.';
            hasErrors = true;
            error.hasErrors = true;
        } else {
            if (distinctText.has(text)) {
                error.text = `This value '${value}' is already present`;
                hasErrors = true;
                error.hasErrors = true;
            } else {
                distinctText.add(text);
            }
        }
        if (!lodash.isNumber(order) || order <= 0) {
            error.order = 'Display Order must be a positive integer.';
            hasErrors = true;
            error.hasErrors = true;
        }
        return error;
    });
    return {
        hasErrors,
        errors
    };
}

function isValidValue(value, dataType) {
    switch (dataType) {
        case 'INTEGER':
            return lodash.isInteger(value);
        case 'REAL':
            return lodash.isNumber(value);
        case 'BOOLEAN':
            return lodash.isBoolean(value);
        default:
            return !lodash.isNil(value) && !lodash.isEmpty(value);
    }
}